import type { WorkflowDefinition } from "../types";

/**
 * W31 — Watch the narrative in a category.
 *
 * A market has a story, and the story is a handful of claims that AI repeats
 * from a handful of sources. This is the message side of W29's ranking: the
 * claims go in as key messages, the awareness-stage prompts are what draw them
 * out, and the citation mix says which publishers the story is coming from.
 *
 * `observed` and `shared`: this is a description of a category that is already
 * set up for messaging work rather than something that starts on a date, and
 * everyone watching the market is watching the same claims.
 */
export const categoryNarrativeWorkflow: WorkflowDefinition = {
	id: "category-narrative",
	wNumber: 31,
	title: "Watch the narrative in a category",
	outcome:
		"Know which claims AI repeats about a market, and which publishers it takes them from.",
	audiences: ["pr"],
	uses: ["Key Messages", "Prompts", "Projects", "Citation Intelligence"],
	scope: "property",
	concurrency: "shared",
	progress: "observed",
	isApplicable: (mode) => mode.isCategory && mode.showKeyMessages,
	articleSlug: "how-to-watch-the-narrative-in-a-category",
	articleStatus: "published",
	status: "live",
	steps: [
		{
			id: "key-messages",
			title: "Add the claims you expect AI to repeat",
			page: "Key Messages",
			route: "/key-messages",
			criterion: "You've added three claims.",
			why: "A market's story is made of a few claims. A claim nobody wrote down cannot be tracked.",
			docsHref: "/docs/dashboards/key-messages",
			tasks: [
				"Write down what this market is supposed to stand for",
				"Keep each claim to a single idea",
				"Add the claims you would rather AI dropped",
			],
			complete: { kind: "count", key: "keyMessages.total", gte: 3 },
			signal: ({ state }) =>
				state.property
					? {
							kind: "count",
							label: "Claims tracked",
							value: state.property.keyMessages.total,
							target: 3,
						}
					: null,
		},
		{
			id: "awareness-prompts",
			title: "Cover the awareness stage",
			page: "Prompts",
			route: "/discovery-queries",
			criterion: "Your prompts cover all three buying stages.",
			why: "Awareness questions are where a market's story shows up, before anyone has a shortlist to compare.",
			docsHref: "/docs/dashboards/discovery-queries",
			tasks: [
				"Add the questions buyers ask before they know the options",
				"Keep the comparison and shortlist questions as well",
				"Tag the awareness set so you can read it on its own",
			],
			complete: { kind: "count", key: "promptStageCount", gte: 3 },
			signal: ({ state }) =>
				state.property
					? {
							kind: "count",
							label: "Buying stages covered",
							value: state.property.promptStageCount,
							target: 3,
							caption: `${state.property.activePromptCount} active prompts`,
						}
					: null,
		},
		{
			id: "track-daily",
			title: "Track the claims every day",
			page: "Projects",
			route: "/projects/new",
			criterion: "You've set up one active project.",
			why: "Claims move slowly. A daily reading is what turns a hunch about the story into a line on a chart.",
			docsHref: "/docs/dashboards/projects",
			tasks: [
				"Create a General project named for the market",
				"Add the category prompts to it",
				"Set the start date",
			],
			complete: { kind: "count", key: "activeProjectCount", gte: 1 },
			spend: { kind: "trackedPrompts", count: "fromState" },
			signal: ({ state }) =>
				state.property
					? {
							kind: "count",
							label: "Active projects",
							value: state.property.activeProjectCount,
							target: 1,
						}
					: null,
		},
		{
			id: "publisher-goals",
			title: "Point goals at the publishers that shape the category",
			page: "Projects",
			route: "/projects",
			criterion: "You've added a goal to an active project.",
			why: "A few outlets set the terms of a market. A goal on those outlets ties the coverage to what AI repeats.",
			docsHref: "/docs/dashboards/projects",
			tasks: [
				"Shortlist the outlets this market's answers cite",
				"Score them before committing outreach hours",
				"Add them as goals on the active project",
			],
			complete: { kind: "count", key: "activeProjectGoalCount", gte: 1 },
			signal: ({ state }) =>
				state.property
					? {
							kind: "count",
							label: "Goals on active projects",
							value: state.property.activeProjectGoalCount,
							target: 1,
						}
					: null,
		},
		{
			id: "trusted-sources",
			title: "Read which sources AI trusts",
			page: "Citation Intelligence",
			route: "/citation-intelligence",
			criterion: "You've reviewed the sources behind the answers.",
			why: "The claims AI repeats come from somewhere. The citation mix shows which publishers the market's story comes from.",
			docsHref: "/docs/dashboards/citation-intelligence",
			tasks: [
				"Read the domains cited most often for this market",
				"Compare them against the outlets you already work with",
				"Start with the sources carrying the claim you want changed",
			],
			complete: { kind: "manual" },
		},
	],
};
